/* eslint-disable no-param-reassign */
import { createAction } from '@reduxjs/toolkit';

import {
  findGalleryOrderEntry,
  PRODUCT_VIEW_LAYER,
} from '../../../components/AssetTargetsDialog/helpers/assetProductLinks';
import { appendProductLink } from '../../helpers/assets/applyAssetProductLinks';
import { setMasterAssets } from './entityAdapter';

export const appendMasterGalleryAssets = createAction('products/appendMasterGalleryAssets');

const collectPoolAssets = (pools = {}) => Object.values(pools).flat().filter(Boolean);

const findAssetInPools = (pools, assetId) => {
  const id = String(assetId);
  return collectPoolAssets(pools).find((asset) => String(asset._id) === id);
};

/**
 * Resolve asset objects for optimistic master gallery insert.
 * Lookup: masterAssets → listingAssets → variant pools → catalog items → stub.
 */
export const resolveAssetsFromState = (state, assetIds = []) => {
  const productAssets = state?.products?.entities?.assets || {};
  const catalogItems = state?.assets?.items || [];

  return assetIds.map((assetId) => {
    const id = String(assetId);
    const fromMaster = findAssetInPools(productAssets.masterAssets, id);
    if (fromMaster) return fromMaster;

    const fromListing = findAssetInPools(productAssets.listingAssets, id);
    if (fromListing) return fromListing;

    const fromMasterVariant = findAssetInPools(productAssets.masterVariantAssets, id);
    if (fromMasterVariant) return fromMasterVariant;

    const fromListingVariant = findAssetInPools(productAssets.listingVariantAssets, id);
    if (fromListingVariant) return fromListingVariant;

    const fromCatalog = catalogItems.find((asset) => String(asset._id) === id);
    if (fromCatalog) return fromCatalog;

    return { _id: assetId, products: [], customOrder: [] };
  });
};

export const getNextMasterCustomOrderIndex = (assets = [], productId) => (
  assets.reduce((max, asset) => {
    const index = findGalleryOrderEntry(asset, productId, PRODUCT_VIEW_LAYER.MASTER)?.index;
    if (!Number.isFinite(index)) return max;
    return index >= max ? index + 1 : max;
  }, 0)
);

const upsertMasterCustomOrder = (customOrder = [], productId, index) => {
  const next = [...customOrder];
  const entryIndex = next.findIndex((entry) => (
    String(entry?.productId) === String(productId) && entry?.isDraft !== true
  ));
  const nextEntry = { productId, index };

  if (entryIndex >= 0) {
    next[entryIndex] = { ...next[entryIndex], ...nextEntry };
  } else {
    next.push(nextEntry);
  }

  return next;
};

/**
 * Build assets to append at the end of the master gallery (skip duplicates).
 */
export const buildOptimisticMasterAssets = ({
  assets = [],
  product,
  existingMasterAssets = [],
} = {}) => {
  if (!product?._id || !assets.length) return [];

  const existingIds = new Set(existingMasterAssets.map((asset) => String(asset._id)));
  let nextIndex = getNextMasterCustomOrderIndex(existingMasterAssets, product._id);
  const masterLink = {
    _id: product._id,
    title: product.title || product._id,
    type: 'master',
  };

  return assets.reduce((acc, asset) => {
    if (!asset?._id || existingIds.has(String(asset._id))) return acc;

    const optimisticAsset = {
      ...asset,
      products: appendProductLink(asset.products, masterLink),
      customOrder: upsertMasterCustomOrder(asset.customOrder, product._id, nextIndex),
    };
    nextIndex += 1;
    existingIds.add(String(asset._id));
    acc.push(optimisticAsset);
    return acc;
  }, []);
};

/**
 * Keep fetched gallery order; re-append mergeAssets missing from search at the end.
 */
export const mergeMasterGalleryAssets = (fetchedAssets = [], mergeAssets = [], productId) => {
  const fetched = Array.isArray(fetchedAssets) ? fetchedAssets : [];
  if (!mergeAssets?.length || !productId) return fetched;

  const fetchedIds = new Set(fetched.map((asset) => String(asset._id)));
  const missing = mergeAssets.filter((asset) => asset?._id && !fetchedIds.has(String(asset._id)));
  if (!missing.length) return fetched;

  let nextIndex = getNextMasterCustomOrderIndex(fetched, productId);
  const appended = missing.map((asset) => {
    const nextAsset = {
      ...asset,
      customOrder: upsertMasterCustomOrder(asset.customOrder, productId, nextIndex),
    };
    nextIndex += 1;
    return nextAsset;
  });

  return [...fetched, ...appended];
};

export const applyAppendMasterGalleryAssetsToState = (state, { productId, assets }) => {
  if (!productId || !assets?.length) return;

  const existing = state.entities?.assets?.masterAssets?.[productId]
    || state.items.find((product) => product._id === productId)?.assets
    || [];
  const existingIds = new Set(existing.map((asset) => String(asset._id)));
  const toAppend = assets.filter((asset) => asset?._id && !existingIds.has(String(asset._id)));
  if (!toAppend.length) return;

  const nextAssets = [...existing, ...toAppend];
  state.items.forEach((product) => {
    if (product._id === productId) {
      product.assets = nextAssets;
    }
  });
  setMasterAssets(state, productId, nextAssets);
};
